import type { CreateContextAttachment } from '@volley/crucible-ipc-types/ipc-channels';
import type {
  CreateContextImageMimeType,
  CreateContextTextMimeType,
} from '@volley/crucible/core/create-context-contract.js';

import { resizeImageFile, type ResizeResult } from './chat/resize-image';

export const CREATE_CONTEXT_FILE_ACCEPT =
  '.md,.txt,.csv,.json,.yaml,.yml,image/png,image/jpeg,image/webp';

/**
 * Renderer-side early feedback. The Electron/Cloud boundary remains
 * authoritative; the parity test keeps these browser-local values in lockstep
 * without leaving a bare workspace-package import in Storybook output.
 */
export const CREATE_CONTEXT_PICKER_LIMITS = {
  maxFiles: 8,
  maxImageBytes: 5 * 1024 * 1024,
  maxTextBytes: 256 * 1024,
  maxTotalTextBytes: 512 * 1024,
  maxTotalBytes: 10 * 1024 * 1024,
} as const;

/** A description or any successfully decoded attachment can seed creation. */
export function hasUsableCreateSeed(
  description: string,
  attachments: readonly CreateContextAttachment[],
): boolean {
  return description.trim().length > 0 || attachments.length > 0;
}

const SAFE_FILE_NAME = /^[^\u0000-\u001f\u007f/\\]{1,180}$/;

const IMAGE_MIMES = new Set<CreateContextImageMimeType>(['image/png', 'image/jpeg', 'image/webp']);

const IMAGE_MIME_BY_EXTENSION: Readonly<Record<string, CreateContextImageMimeType>> = {
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.webp': 'image/webp',
};

const TEXT_MIME_BY_EXTENSION: Readonly<Record<string, CreateContextTextMimeType>> = {
  '.md': 'text/markdown',
  '.txt': 'text/plain',
  '.csv': 'text/csv',
  '.json': 'application/json',
  '.yaml': 'application/yaml',
  '.yml': 'application/yaml',
};

function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot).toLowerCase() : '';
}

function resizedFrom(
  result: ResizeResult,
): { width: number; height: number; finalWidth: number; finalHeight: number } | undefined {
  return result.didResize
    ? {
        width: result.originalWidth,
        height: result.originalHeight,
        finalWidth: result.finalWidth,
        finalHeight: result.finalHeight,
      }
    : undefined;
}

/** Convert picker files into the shared, Cloud-safe create attachment contract. */
export async function readCreateContextFiles(
  current: readonly CreateContextAttachment[],
  files: readonly File[],
  resizeImage: (file: File) => Promise<ResizeResult> = resizeImageFile,
): Promise<CreateContextAttachment[]> {
  if (current.length + files.length > CREATE_CONTEXT_PICKER_LIMITS.maxFiles) {
    throw new Error(`Add no more than ${CREATE_CONTEXT_PICKER_LIMITS.maxFiles} context files.`);
  }

  const added: CreateContextAttachment[] = [];
  for (const file of files) {
    const fileName = file.name.trim();
    if (!SAFE_FILE_NAME.test(fileName)) throw new Error(`${file.name} has an invalid filename.`);
    const inferredImageMime = IMAGE_MIME_BY_EXTENSION[extensionOf(fileName)];
    if (IMAGE_MIMES.has(file.type as CreateContextImageMimeType) || inferredImageMime) {
      const image = await resizeImage(file);
      if (image.originalWidth <= 0 || image.originalHeight <= 0) {
        throw new Error(`${fileName} could not be decoded as an image.`);
      }
      const imageMime = IMAGE_MIMES.has(image.mimeType as CreateContextImageMimeType)
        ? (image.mimeType as CreateContextImageMimeType)
        : inferredImageMime;
      if (!imageMime) {
        throw new Error(`${fileName} must be a PNG, JPEG, or WebP image.`);
      }
      if (image.byteLength > CREATE_CONTEXT_PICKER_LIMITS.maxImageBytes) {
        throw new Error(`${fileName} is larger than the 5 MB image limit.`);
      }
      const resizeMetadata = resizedFrom(image);
      added.push({
        kind: 'image',
        name: fileName,
        mimeType: imageMime,
        base64: image.base64,
        byteLength: image.byteLength,
        ...(resizeMetadata ? { resizedFrom: resizeMetadata } : {}),
      });
      continue;
    }

    const textMime = TEXT_MIME_BY_EXTENSION[extensionOf(fileName)];
    if (!textMime) {
      throw new Error(`${fileName} must be a text document or PNG, JPEG, or WebP image.`);
    }
    if (file.size > CREATE_CONTEXT_PICKER_LIMITS.maxTextBytes) {
      throw new Error(`${fileName} is larger than the 256 KB text-file limit.`);
    }
    const text = await file.text();
    const byteLength = new TextEncoder().encode(text).byteLength;
    added.push({ kind: 'text', name: fileName, mimeType: textMime, text, byteLength });
  }

  const next = [...current, ...added];
  const totalBytes = next.reduce((sum, attachment) => sum + attachment.byteLength, 0);
  const totalTextBytes = next.reduce(
    (sum, attachment) => sum + (attachment.kind === 'text' ? attachment.byteLength : 0),
    0,
  );
  if (totalBytes > CREATE_CONTEXT_PICKER_LIMITS.maxTotalBytes) {
    throw new Error('Context files are larger than the 10 MB total limit.');
  }
  if (totalTextBytes > CREATE_CONTEXT_PICKER_LIMITS.maxTotalTextBytes) {
    throw new Error('Text context is larger than the 512 KB total limit.');
  }
  return next;
}
